import { useQuery } from "react-query";
import { request } from "../../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../hooks/request-error";

// useTatOrders — GET /api/ops/tat/orders against the real pillup-api backend
// (pillup-api/controller/ops/tat.js, contract documented in
// pillup-api/docs/tat-dashboard/reference-tat-api.md).
//
// Two adapters happen in this hook, both because the wire contract and this
// page's internal shape were designed independently and don't match 1:1:
//
// 1. Envelope: every pillup-api response is `{success, data, error}` at
//    HTTP 200 EVEN ON FAILURE — errors are in-band (utils/utils.js's
//    errorHandler calls `res.send()` with no status code, which Express
//    defaults to 200). `throwIfRequestError` only catches a non-2xx HTTP
//    status or a genuine network error; it does NOT see `success: false`
//    inside a 200 body. So this hook must check `success` itself.
//
// 2. Row shape: the real API returns three parallel objects per order
//    (`stages`, `tat`, `tatState`) "so the client never infers meaning from
//    a null" (reference-tat-api.md). TatCell/TatTable were built and tested
//    against one merged `intervals: { [key]: {state, minutes, note} }` dict
//    instead — that contract is good (it's what makes TatCell a single
//    source of truth for the seven-state rendering rules) and cheap to keep:
//    the merge happens here, once, rather than teaching every consumer to
//    zip three objects together.
//
// The `url` passed to request() is `/ops/tat/orders`, not `/api/ops/tat/orders`
// — request()'s baseURL (REACT_APP_BACKEND_URL) already ends in `/api/`.

/**
 * Normalize a raw filters object into the exact query-param shape the
 * contract documents.
 *
 * @param {object} filters
 * @param {string} [filters.from]
 * @param {string} [filters.to]
 * @param {string} [filters.q]
 * @param {string} [filters.partnerType] - DIRECT | PARTNER | ALL. Defaults server-side
 *   to DIRECT; this page always states it explicitly — see below.
 * @param {string} [filters.partnerSource] - one of the server's partnerSource enum
 *   values (VISIT, TATVAPRACTICE, GLP_1, NUTRAPACK). Forwarded as given; the page is
 *   what validates it against TAT_PARTNER_SOURCES before calling here.
 * @param {string} [filters.statusGroup] - a key from useTatFilters' `statusGroups`
 *   (e.g. "TO_PROCESS", "IN_PROGRESS") — the server does the expansion (or, for
 *   IN_PROGRESS, the exclusion) itself; this hook sends the key verbatim and
 *   carries no copy of which codes belong to which group.
 */
export function buildTatFilterParams({ from, to, q, statusGroup, sort, partnerType, partnerSource } = {}) {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    // The real endpoint's search param is `search`, not `q` — kept as `q` in
    // this hook's own public API (and the URL) since that's what index.jsx's
    // search box already writes to the URL bar.
    if (q) params.search = q;
    // The backend's SORT_SQL keys (controller/ops/tat.js) are exactly the ids in
    // src/v2/ops/constants/tatFilters.js, so this passes straight through. It defaults to
    // `recent` server-side, so only send a non-default.
    if (sort && sort !== "recent") params.sort = sort;
    // "all" (this page's own default, not a server concept) means no filter at all.
    if (statusGroup && statusGroup !== "all") params.statusGroup = statusGroup;

    // Sent even when it equals the server's own default (DIRECT), which is a deliberate
    // break from how `sort` is handled above. `sort`'s default means "however the server
    // likes"; DIRECT is a substantive data filter that decides which orders exist on the
    // page at all. A request whose params do not say which population it asked for is a
    // request nobody can debug from a log.
    if (partnerType) params.partnerType = partnerType;

    // `partnerId` used to be built here too and is gone rather than left unused: the
    // picker that produced it was replaced by partnerSource, and a param nothing can
    // emit is a line that quietly stops being true. The endpoint still accepts it.
    if (partnerSource) params.partnerSource = partnerSource;

    return params;
}

/** Real API -> this page's TatCell/TatTable contract, one order at a time. */
function toRow(order) {
    const intervalKeys = new Set([...Object.keys(order.tat || {}), ...Object.keys(order.tatState || {})]);
    intervalKeys.delete("openAgeMinutes"); // informational, not a column
    const intervals = {};
    intervalKeys.forEach((key) => {
        intervals[key] = { state: order.tatState?.[key], minutes: order.tat?.[key] ?? null };
    });

    return {
        id: order.orderId,
        patientName: order.patientName || "—",
        // `address`/`pincode` are on every real row — a second identifier
        // alongside patientName for the (still common) row with no linked name.
        address: [order.address, order.pincode].filter(Boolean).join(", ") || "—",
        status: order.orderStatus,
        // Where the order came from (VISIT, TATVAPRACTICE, META, APP, INTERNAL, ...).
        // This replaced `partnerName` in the table's identity columns. Note it is not a
        // partner-only field: a direct order carries a source too, which is why the
        // column it feeds is unconditional where the partner one was not.
        source: order.source ?? null,
        intervals,
        // Passed through raw (not flattened to a boolean here) so TatTable's
        // render logic — not this adapter — owns the "is this worth flagging"
        // judgment call. See TatTable.jsx's dataQualityWarning.
        dataQuality: order.dataQuality || null,
        // Raw status-transition log, present only when `includeTimeline` was sent
        // (and only once the backend supports it). `undefined` is preserved as
        // `undefined` rather than normalised to `[]`: TatTimelineModal renders
        // "we didn't get a timeline" differently from "this order has no
        // transitions", and collapsing the two here would make that impossible.
        timeline: order.timeline,
        // The raw stage timestamps, keyed by stage name (`orderConfirmedAt`,
        // `packedAt`, ...). `intervals` above answers "how long did this span take";
        // only this answers "and when did it happen". Kept raw and unmerged because
        // `meta.intervals[].from`/`.to` are what index into it.
        stages: order.stages || {},
    };
}

/**
 * @param {object} filters - same shape as buildTatFilterParams, plus:
 * @param {number} [filters.page] - 1-based page number, defaults to 1.
 * @param {number} [filters.size] - page size, defaults to 20 (matches the API default).
 * @param {boolean} [filters.enabled] - defaults to true. Set false to defer
 *   firing this query.
 * @param {boolean} [filters.includeTimeline] - defaults to true; see below.
 */
export default function useTatOrders(filters = {}) {
    const { page = 1, size = 20, enabled = true, includeTimeline = true } = filters;
    const params = { ...buildTatFilterParams(filters), page, size };
    // ON, because the stage drill-down attributes each stage to whoever completed it and
    // `timeline` is the only place an actor name exists. It is NOT fetched to be dumped
    // on screen: of the 13 transitions a real order carries, typically two bear a
    // `stage` key, and those are the only ones read (see buildStageActors). The rest —
    // the ORDER_ACTIVE/ORDER_PENDING churn and the duplicate CONVERTED_TO_ORDER writes —
    // stay unrendered.
    //
    // Fetched with the page rather than per row because this IS the list endpoint:
    // fetching one order's timeline would mean re-requesting the whole page. Asking once
    // means the dialog opens with its answer rather than a spinner.
    if (includeTimeline) params.includeTimeline = true;

    return useQuery(
        ["tat-orders", params],
        async () => {
            const result = await request({
                url: "/ops/tat/orders",
                method: "GET",
                params,
            });
            throwIfRequestError(result);
            const body = result.data;
            if (!body?.success) throw body;

            const { meta, pagination, orders } = body.data;
            return {
                meta: {
                    // Why a stage is not instrumented, straight from the server
                    // (`{key, reason, detail}`). Without it a NOT_TRACKED cell states a
                    // fact and withholds its reason, which is its own small confusion.
                    unavailableStages: meta.unavailableStages || [],
                    // `from`/`to` (the stage keys each interval is measured between) and
                    // `available` used to be dropped here, which cost the drill-down the
                    // ability to say WHEN a stage boundary happened rather than only how
                    // long the span took. Passed through now; TatTable still reads only
                    // `key`/`label` and is unaffected.
                    intervals: meta.intervals.map(({ key, label, from, to, available }) => ({
                        key,
                        label,
                        from,
                        to,
                        available,
                    })),
                },
                pagination,
                orders: orders.map(toRow),
            };
        },
        {
            // Keeps the previous page's rows on screen while a new page/filter
            // combination loads, instead of flashing a skeleton on every click.
            keepPreviousData: true,
            retry: false,
            enabled,
        }
    );
}
